import {
  CameraConfig,
  CameraStatus,
} from '../api/camera';

export type CameraFieldErrors = Partial<Record<keyof CameraConfig | string, string>>;

export function exposureUsToMs(value: number): number {
  return value / 1000;
}

export function exposureMsToUs(value: number): number {
  return Math.round(value * 1000);
}

function rangeError(
  value: number | null | undefined,
  label: string,
  min: number,
  max: number,
): string | undefined {
  if (value === null || value === undefined || !Number.isFinite(value)) return `${label} חייב להיות מספר`;
  if (value < min || value > max) {
    return `${label} חייב להיות בין ${min} ל-${max}`;
  }
}

export function validateCameraConfig(config: CameraConfig): CameraFieldErrors {
  const errors: CameraFieldErrors = {};
  const set = (field: string, message?: string) => {
    if (message) errors[field] = message;
  };
  set('exposureTimeUs', rangeError(config.exposureTimeUs, 'זמן חשיפה', 100, 10_000_000));
  set('analogueGain', rangeError(config.analogueGain, 'Gain', 1, 64));
  set('frameDurationUs', rangeError(config.frameDurationUs, 'משך פריים', 1000, 10_000_000));
  set('flickerPeriodUs', rangeError(config.flickerPeriodUs, 'מחזור הבהוב', 1000, 100_000));

  if (config.exposureMode === 'manual') {
    if (!errors.exposureTimeUs && !errors.frameDurationUs && config.exposureTimeUs > config.frameDurationUs) {
      errors.exposureTimeUs = 'זמן החשיפה לא יכול להיות ארוך ממשך הפריים';
    }
  }
  if (config.flickerAvoidanceEnabled) {
    if (!config.antiBandingEnabled) errors.antiBandingEnabled = 'מניעת banding נדרשת כאשר מניעת הבהוב פעילה';
    if (!errors.flickerPeriodUs && !errors.frameDurationUs && config.flickerPeriodUs > config.frameDurationUs) {
      errors.flickerPeriodUs = 'מחזור ההבהוב לא יכול להיות ארוך ממשך הפריים';
    }
  }
  if (
    config.exposureMode === 'manual'
    && config.antiBandingEnabled
    && !errors.exposureTimeUs
    && !errors.frameDurationUs
    && !errors.flickerPeriodUs
  ) {
    const timingQuantum = Math.max(1, Math.floor(config.flickerPeriodUs / 2));
    if (config.exposureTimeUs % timingQuantum !== 0) {
      errors.exposureTimeUs = `זמן החשיפה חייב להיות כפולה של ${timingQuantum}µs`;
    }
    if (config.frameDurationUs % timingQuantum !== 0) {
      errors.frameDurationUs = `משך הפריים חייב להיות כפולה של ${timingQuantum}µs`;
    }
  }
  if (config.colourGainRed != null) {
    set('colourGainRed', rangeError(config.colourGainRed, 'Gain אדום', 0.1, 32));
  }
  if (config.colourGainBlue != null) {
    set('colourGainBlue', rangeError(config.colourGainBlue, 'Gain כחול', 0.1, 32));
  }
  if (config.whiteBalanceMode === 'manual') {
    if (config.colourGainRed == null) errors.colourGainRed = 'Gain אדום נדרש באיזון לבן ידני';
    if (config.colourGainBlue == null) errors.colourGainBlue = 'Gain כחול נדרש באיזון לבן ידני';
  }
  if ((config.colourGainRed == null) !== (config.colourGainBlue == null)) {
    errors.colourGainRed ||= 'יש להזין Gain אדום וכחול יחד';
    errors.colourGainBlue ||= 'יש להזין Gain אדום וכחול יחד';
  }
  if (config.lensPosition != null) {
    set('lensPosition', rangeError(config.lensPosition, 'מיקום עדשה', 0, Number.MAX_SAFE_INTEGER));
  }
  if (config.focusMode === 'manual') {
    if (config.lensPosition == null) errors.lensPosition = 'מיקום עדשה נדרש בפוקוס ידני';
  }
  if (config.zoom != null) set('zoom', rangeError(config.zoom, 'זום', 1, 32));
  if (config.pan != null) set('pan', rangeError(config.pan, 'Pan', -1, 1));
  if (config.tilt != null) set('tilt', rangeError(config.tilt, 'Tilt', -1, 1));
  if (config.brightness != null) set('brightness', rangeError(config.brightness, 'בהירות', -1, 1));
  if (config.contrast != null) set('contrast', rangeError(config.contrast, 'ניגודיות', 0, 32));
  if (config.framesPerSecond != null) {
    if (config.framesPerSecond <= 0 || config.framesPerSecond > 240) {
      errors.framesPerSecond = 'FPS חייב להיות גדול מ-0 ועד 240';
    }
  }
  set('warmupSeconds', rangeError(config.warmupSeconds, 'חימום', 0, 60));
  if (config.rotation != null && ![0, 90, 180, 270].includes(config.rotation)) {
    errors.rotation = 'סיבוב חייב להיות 0, 90, 180 או 270 מעלות';
  }
  if ((config.resolutionWidth == null) !== (config.resolutionHeight == null)) {
    errors.resolutionWidth = 'יש להזין רוחב וגובה יחד';
    errors.resolutionHeight = 'יש להזין רוחב וגובה יחד';
  }
  if (config.resolutionWidth != null && (!Number.isInteger(config.resolutionWidth) || config.resolutionWidth < 160 || config.resolutionWidth > 16384)) {
    errors.resolutionWidth = 'רוחב חייב להיות מספר שלם בין 160 ל-16384';
  }
  if (config.resolutionHeight != null && (!Number.isInteger(config.resolutionHeight) || config.resolutionHeight < 120 || config.resolutionHeight > 16384)) {
    errors.resolutionHeight = 'גובה חייב להיות מספר שלם בין 120 ל-16384';
  }
  if (
    config.calibrationExposureTimesUs.length < 1
    || config.calibrationExposureTimesUs.length > 32
    || config.calibrationExposureTimesUs.some((value) => !Number.isInteger(value) || value <= 0)
    || new Set(config.calibrationExposureTimesUs).size !== config.calibrationExposureTimesUs.length
  ) {
    errors.calibrationExposureTimesUs = 'יש להזין רשימת חשיפות ייחודית';
  }
  return errors;
}

export function isCameraConfigApplied(status?: CameraStatus | null): boolean {
  return !!status
    && status.configurationVersion > 0
    && status.lastAppliedVersion === status.configurationVersion
    && status.status === 'active'
    && !status.error;
}

export function cameraStatusTone(status?: CameraStatus | null): 'ok' | 'pending' | 'error' | 'offline' {
  if (!status?.online) return 'offline';
  if (status.error || status.status === 'failed') return 'error';
  if (!isCameraConfigApplied(status)) return 'pending';
  return 'ok';
}

export function cameraStatusRevision(status?: CameraStatus | null): string {
  if (!status) return '';
  return [
    status.configurationVersion,
    status.status,
    status.deliveredVersion ?? '',
    status.deliveryState ?? '',
    status.lastAppliedVersion ?? '',
    status.acknowledgedAt ?? '',
    status.pendingCommandId ?? '',
    status.error ?? '',
    ...(status.warnings || []),
  ].join('|');
}

export function cameraDeliveryInfo(status?: CameraStatus | null): {
  version: number | null;
  state: string | null;
} {
  if (!status) return { version: null, state: null };
  const state = status.deliveryState
    || (status.pendingCommandId ? 'pending' : status.acknowledgedAt ? 'acknowledged' : status.status);
  const currentWasDelivered = ['dispatched', 'succeeded', 'failed', 'active', 'acknowledged']
    .includes(state);
  return {
    version: status.deliveredVersion
      ?? (currentWasDelivered ? status.configurationVersion : status.lastAppliedVersion ?? null),
    state,
  };
}

export function uniqueExposureTimesMs(values: number[]): number[] {
  return [...new Set(values.filter((value) => Number.isFinite(value) && value > 0))]
    .sort((a, b) => a - b);
}
